import React, { useEffect, useState } from "react";
import { TextField, MenuItem, IconButton, Button, Menu, Autocomplete, Grid, Checkbox } from "@mui/material";
import { MdDownload, MdSearch, MdRefresh, MdPerson } from "react-icons/md";
import { PictureAsPdf, TableChart } from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { fetchByTable, fetchQuery } from "./fetched";
import { SearchToggleButton } from "../Custom/Button";
import moment from "moment";
import { branchOptions, departmentOptions } from "../../components/Pages/employeeMaster/employeeConstants";

const reportOptions = [
  { id: 1, name: "Attendance Report" },
  { id: 2, name: "Daily Detail Attendance Report" },
  { id: 3, name: "Late Coming Attendance Report" },
  { id: 4, name: "Early Going Attendance Report" },
];

const ReportToolbar = ({
  inputStates,
  updateInputObject,
  searched,
  handleSearch,
  clearSearch,
  permissions,
}) => {
  const [employeesData, setEmployeesData] = useState([]);
  const _employeeId = Number(localStorage.getItem("employeeId"));
  const _userType = Number(localStorage.getItem("type"));

  const fetchEmployeesData = async () => {
    try {
      let whereConditions = "";

      // 👤 User-type restriction
      if (_userType !== 1 && _userType !== 2 && _userType !== 8) {
        whereConditions = ` WHERE e.id = ${_employeeId}`;
      }

      // 🏢 Department condition
      if (inputStates?.department) {
        whereConditions += whereConditions
          ? ` AND eod.department = '${inputStates.department}'`
          : ` WHERE eod.department = '${inputStates.department}'`;
      }

      // 🏬 Branch condition
      if (inputStates?.branch) {
        whereConditions += whereConditions
          ? ` AND eod.branchId = '${inputStates.branch}'`
          : ` WHERE eod.branchId = '${inputStates.branch}'`;
      }

      const Employees = await fetchQuery(`
      SELECT 
        e.id, 
        e.employeesCode, 
        e.name
      FROM employees e
      LEFT JOIN employeeOtherData eod 
        ON eod.employeeId = e.id
      ${whereConditions}
    `);

      setEmployeesData(Employees);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    fetchEmployeesData();
  }, [inputStates?.department, inputStates?.branch]);

  useEffect(() => {
    if (
      _userType !== 1 &&
      _userType !== 2 &&
      _userType !== 8 &&
      employeesData.length > 0
    ) {
      updateInputObject("employeeIds", [employeesData[0].id]);
    }
  }, [_userType, employeesData]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 p-4 bg-white shadow-md rounded-lg">
      {/* ================= FIRST ROW (ONLY FOR USER TYPE 1,2,8) ================= */}
      {[1, 2, 8].includes(_userType) && (
        <>
          {/* Report Type */}
          <div className="lg:col-span-4">
            <Autocomplete
              size="small"
              fullWidth
              options={reportOptions}
              value={
                reportOptions.find(
                  (opt) => opt.id === inputStates.reportTypeId
                ) || null
              }
              getOptionLabel={(option) => option.name}
              onChange={(event, value) =>
                updateInputObject("reportTypeId", value ? value.id : "")
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Report Type" />
              )}
            />
          </div>

          {/* Department */}
          <div className="lg:col-span-4">
            <Autocomplete
              size="small"
              fullWidth
              options={departmentOptions}
              value={
                departmentOptions.find(
                  (opt) => opt.name === inputStates.department
                ) || null
              }
              getOptionLabel={(option) => option.name}
              onChange={(event, value) =>
                updateInputObject("department", value ? value.name : "")
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Department" />
              )}
            />
          </div>

          {/* Branch */}
          <div className="lg:col-span-4">
            <Autocomplete
              size="small"
              fullWidth
              options={branchOptions}
              value={
                branchOptions.find((opt) => opt.name === inputStates.branch) ||
                null
              }
              getOptionLabel={(option) => option.name}
              onChange={(event, value) =>
                updateInputObject("branch", value ? value.name : "")
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Branch" />
              )}
            />
          </div>
        </>
      )}

      {/* ================= SECOND ROW ================= */}

      {/* Employee (ONLY FOR USER TYPE 1,2,8) */}
      {[1, 2, 8].includes(_userType) && (
        <div className="lg:col-span-4">
          <Autocomplete
            multiple
            limitTags={1}
            size="small"
            disableCloseOnSelect
            fullWidth
            options={[{ id: "ALL", name: "Select All" }, ...employeesData]}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            getOptionLabel={(option) =>
              option.id === "ALL"
                ? "Select All"
                : `${option.employeesCode ? option.employeesCode + " - " : ""}${option.name
                }`
            }
            value={employeesData.filter((emp) =>
              inputStates.employeeIds?.includes(emp.id)
            )}
            onChange={(event, selectedOptions) => {
              const hasSelectAll = selectedOptions.some(
                (opt) => opt.id === "ALL"
              );

              updateInputObject(
                "employeeIds",
                hasSelectAll
                  ? employeesData.map((emp) => emp.id)
                  : selectedOptions.map((emp) => emp.id)
              );
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  sx={{ mr: 1 }}
                  checked={
                    option.id === "ALL"
                      ? inputStates.employeeIds?.length === employeesData.length
                      : selected
                  }
                />
                {option.id === "ALL"
                  ? "Select All"
                  : `${option.employeesCode ? option.employeesCode + " - " : ""
                  }${option.name}`}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Particular Employee"
                placeholder="Select employee(s)"
              />
            )}
          />
        </div>
      )}

      {/* From Date */}
      <div className="lg:col-span-3">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From Date"
            value={inputStates.fromDate || null}
            format="dd/MM/yyyy"
            maxDate={new Date()}
            onChange={(newValue) => updateInputObject("fromDate", newValue)}
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
        </LocalizationProvider>
      </div>

      {/* To Date */}
      <div className="lg:col-span-3">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="To Date"
            value={inputStates.toDate || null}
            format="dd/MM/yyyy"
            maxDate={new Date()}
            onChange={(newValue) => updateInputObject("toDate", newValue)}
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
        </LocalizationProvider>
      </div>

      {/* Search Button */}
      <div className="lg:col-span-2 flex items-center justify-center">
        <SearchToggleButton
          searched={searched}
          onSearch={handleSearch}
          onClear={clearSearch}
        />
      </div>
    </div>
  );
};

export default ReportToolbar;
